import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Link, Navigate, useLocation, useRouteError } from 'react-router';
import { ShieldOff, Compass } from 'lucide-react';
import type { Capability, Role } from '@skhub/contracts';
import { can, isStaff } from '@skhub/contracts';
import { useAuth } from './auth';
import { AppShell } from '@/components/AppShell';
import { BrandLoader } from '@/components/brand';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui';
import { LoginPage } from '@/features/login/LoginPage';

const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const LaptopsPage = lazy(() => import('@/features/laptops/LaptopsPage'));
const AssetsPage = lazy(() => import('@/features/assets/AssetsPage'));
const AssetDetailPage = lazy(() => import('@/features/assets/AssetDetailPage'));
const AssetCreatePage = lazy(() => import('@/features/assets/AssetCreatePage'));
const EmployeesPage = lazy(() => import('@/features/employees/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('@/features/employees/EmployeeDetailPage'));
const MyDevicesPage = lazy(() => import('@/features/me/MyDevicesPage'));
const AuditPage = lazy(() => import('@/features/audit/AuditPage'));
const ImportPage = lazy(() => import('@/features/import/ImportPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const SoftwareOverviewPage = lazy(() => import('@/features/software/SoftwareOverviewPage'));
const LicensesPage = lazy(() => import('@/features/software/LicensesPage'));
const LicenseDetailPage = lazy(() => import('@/features/software/LicenseDetailPage'));
const SeatsPage = lazy(() => import('@/features/software/SeatsPage'));
const WarrantyPage = lazy(() => import('@/features/warranty/WarrantyPage'));
const MovementsPage = lazy(() => import('@/features/reports/MovementsPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));

/** Auditors start on the audit trail; other staff on the dashboard; employees on their own devices. */
export const homeFor = (role: Role) => (role === 'Auditor' ? '/audit' : isStaff(role) ? '/dashboard' : '/my-devices');

/** Capability each top-level area needs; mirrors the <Gate> on each route below. */
const AREA_CAPS: [prefix: string, cap: Capability][] = [
  ['/dashboard', 'insights.read'],
  ['/laptops', 'insights.read'],
  ['/warranty', 'insights.read'],
  ['/assets/new', 'inventory.write'],
  ['/assets', 'inventory.read'],
  ['/employees', 'people.read'],
  ['/software/licenses', 'licenses.read'],
  ['/software/seats', 'licenses.read'],
  ['/software', 'insights.read'],
  ['/my-devices', 'self.devices'],
  ['/audit', 'audit.read'],
  ['/import', 'import.run'],
  ['/reports', 'reports.export'],
  ['/settings', 'settings.read'],
];

/**
 * Where to go after sign-in. The page someone was sent away from is honoured only if this role may
 * open it; otherwise (e.g. an employee signing in after a staff session ended on /dashboard) the
 * role's own home, so nobody lands on "This area is restricted".
 */
export function landingFor(role: Role, from: string | null | undefined): string {
  if (!from || !from.startsWith('/') || from.startsWith('//') || from === '/' || from.startsWith('/login')) return homeFor(role);
  const path = from.split(/[?#]/)[0]!;
  const area = AREA_CAPS.find(([p]) => path === p || path.startsWith(`${p}/`));
  return area && !can(role, area[1]) ? homeFor(role) : from;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading, error, refresh } = useAuth();
  const location = useLocation();
  if (loading) return <BrandLoader />;
  if (error)
    return (
      <div className="grid min-h-dvh place-items-center">
        <ErrorState error={error} onRetry={() => void refresh()} />
      </div>
    );
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function Gate({ cap, children }: { cap: Capability; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(cap)) return <Forbidden />;
  return (
    <Suspense fallback={<PageSkeleton />}>
      {children}
    </Suspense>
  );
}

function Home() {
  const { session } = useAuth();
  return <Navigate to={session ? homeFor(session.user.role) : '/login'} replace />;
}

function PageSkeleton() {
  return (
    <div role="status" aria-label="Loading page" className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-4 md:grid-cols-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-72" />
    </div>
  );
}

function Forbidden() {
  const { session } = useAuth();
  const home = session ? homeFor(session.user.role) : '/login';
  return (
    <EmptyState
      icon={ShieldOff}
      title="This area is restricted"
      action={
        <Link to={home} className="btn btn-secondary btn-sm">
          {home === '/my-devices' ? 'Go to My devices' : 'Go to dashboard'}
        </Link>
      }
    >
      Your role does not include access to this page. If you need it, ask the SmartKargo IT Team.
    </EmptyState>
  );
}

function NotFound() {
  return (
    <EmptyState icon={Compass} title="Page not found">
      The address may be mistyped, or the page has moved.
    </EmptyState>
  );
}

function RouteError() {
  const err = useRouteError();
  return (
    <div className="grid min-h-dvh place-items-center">
      <ErrorState error={err} onRetry={() => window.location.reload()} />
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Home /> },
      { path: 'dashboard', element: <Gate cap="insights.read"><DashboardPage /></Gate> },
      { path: 'laptops', element: <Gate cap="insights.read"><LaptopsPage /></Gate> },
      { path: 'assets', element: <Gate cap="inventory.read"><AssetsPage /></Gate> },
      { path: 'assets/new', element: <Gate cap="inventory.write"><AssetCreatePage /></Gate> },
      { path: 'assets/:id', element: <Gate cap="inventory.read"><AssetDetailPage /></Gate> },
      { path: 'employees', element: <Gate cap="people.read"><EmployeesPage /></Gate> },
      { path: 'employees/:id', element: <Gate cap="people.read"><EmployeeDetailPage /></Gate> },
      { path: 'software', element: <Gate cap="insights.read"><SoftwareOverviewPage /></Gate> },
      { path: 'software/licenses', element: <Gate cap="licenses.read"><LicensesPage /></Gate> },
      { path: 'software/licenses/:id', element: <Gate cap="licenses.read"><LicenseDetailPage /></Gate> },
      { path: 'software/seats', element: <Gate cap="licenses.read"><SeatsPage /></Gate> },
      { path: 'my-devices', element: <Gate cap="self.devices"><MyDevicesPage /></Gate> },
      { path: 'audit', element: <Gate cap="audit.read"><AuditPage /></Gate> },
      { path: 'import', element: <Gate cap="import.run"><ImportPage /></Gate> },
      { path: 'reports', element: <Gate cap="reports.export"><ReportsPage /></Gate> },
      { path: 'reports/movements', element: <Gate cap="reports.export"><MovementsPage /></Gate> },
      { path: 'warranty', element: <Gate cap="insights.read"><WarrantyPage /></Gate> },
      { path: 'settings', element: <Gate cap="settings.read"><SettingsPage /></Gate> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
